// Who is signed in, in the product's words (PRODUCT.md): MotionSafe staff,
// who are admins (they change anything) or staff holding staff roles
// (Support, Fulfillment, Accounts; none is read only); and an organization's
// administrators. The service refuses what a role does not allow
// (api/src/admin/staff-roles.ts, served by ListStaffRoles); these only
// decide what the console offers, so a control is locked before it is
// refused, with a caption naming the role it needs.

import type { ListStaffRolesResponse } from '@motionsafe/ms-console-api/admin/v1/user_pb.js';
import { ConsoleRole, StaffRole } from '@motionsafe/ms-console-api/common/v1/common_pb.js';

import type { Me } from '../api/client';

type Signed = Pick<Me, 'consoleRole'>;

/** MotionSafe staff of either kind: they see every organization. */
export function isStaff(me: Signed): boolean {
  return me.consoleRole === 'ms_superadmin' || me.consoleRole === 'ms_staff';
}

/** A staff account's role, from the session or from a roster row. */
export function isStaffRole(role: ConsoleRole | Me['consoleRole']): boolean {
  return role === ConsoleRole.MS_SUPERADMIN || role === ConsoleRole.MS_STAFF || role === 'ms_superadmin' || role === 'ms_staff';
}

/** The staff roles in the order the console lists them. */
export const STAFF_ROLE_ORDER = [StaffRole.SUPPORT, StaffRole.FULFILLMENT, StaffRole.ACCOUNTS] as const;

const ROLE_NAMES: Record<StaffRole, string> = {
  [StaffRole.UNSPECIFIED]: '',
  [StaffRole.SUPPORT]: 'Support',
  [StaffRole.FULFILLMENT]: 'Fulfillment',
  [StaffRole.ACCOUNTS]: 'Accounts',
};

export function staffRoleName(role: StaffRole): string {
  return ROLE_NAMES[role];
}

/** The session's staff roles, which `/api/me` gives as row words ("support"). */
export function sessionStaffRoles(me: Pick<Me, 'staffRoles'>): StaffRole[] {
  return (me.staffRoles ?? []).map((r) => StaffRole[r.toUpperCase() as keyof typeof StaffRole]).filter((r): r is StaffRole => typeof r === 'number' && r !== StaffRole.UNSPECIFIED);
}

/** Role names joined for a sentence: "Support", "Support and Fulfillment". */
export function joinRoles(roles: readonly StaffRole[], word: 'and' | 'or' = 'and'): string {
  const names = STAFF_ROLE_ORDER.filter((r) => roles.includes(r)).map(staffRoleName);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} ${word} ${names.at(-1)}` : names[0] ?? '';
}

/** The word for a member of staff's role, from a roster row: "Admin", "Read only", or their roles. */
export function staffRoleLabel(role: ConsoleRole | Me['consoleRole'], roles: readonly StaffRole[] = []): string {
  if (role === ConsoleRole.MS_SUPERADMIN || role === 'ms_superadmin') return 'Admin';
  return roles.length ? joinRoles(roles) : 'Read only';
}

/** The staff organization's slug: MotionSafe itself, whose administrators are the staff. */
export const STAFF_ORG_SLUG = 'motionsafe';

export interface Access {
  /** Admins and organization administrators, whom the staff roles table does not limit. */
  unrestricted: boolean;
  /** Whether the person signed in may make this change (an admin RPC method name, "AssignDevice"). */
  may: (method: string) => boolean;
  /** The caption under a control they may not use: the role it needs, or that it is an admin's. */
  caption: (method: string) => string;
}

// The table's read rule compiled and its rows keyed by method, once per table:
// every line of every row's menu asks.
const INDEXES = new WeakMap<ListStaffRolesResponse, { reads: RegExp; byMethod: Map<string, ListStaffRolesResponse['capabilities'][number]> }>();
function indexOf(table: ListStaffRolesResponse) {
  let index = INDEXES.get(table);
  if (!index) {
    index = { reads: new RegExp(table.readMethods), byMethod: new Map(table.capabilities.flatMap((c) => c.methods.map((m) => [m, c] as const))) };
    INDEXES.set(table, index);
  }
  return index;
}

/** What only an admin may change says so, and who to ask. */
export const ADMIN_CAPTION = 'Only a MotionSafe admin can change this.';

/** What staff who are not admins see on the MotionSafe organization and its people: never theirs to change. */
export const STAFF_ORG_CAPTION = 'Only a MotionSafe admin changes the MotionSafe organization or its people.';

/**
 * Pure: what the person signed in may change, from their session and the
 * staff roles table. Admins and organization administrators are not asked:
 * the services hold their limits. Until the table has loaded, staff who are
 * not admins see every change locked, which is what the service would say.
 */
export function accessFor(me: Pick<Me, 'consoleRole' | 'staffRoles'>, table: ListStaffRolesResponse | undefined): Access {
  if (me.consoleRole !== 'ms_staff') return { unrestricted: true, may: () => true, caption: () => '' };
  const held = sessionStaffRoles(me);
  const index = table ? indexOf(table) : undefined;
  const rowOf = (method: string) => index?.byMethod.get(method);
  return {
    unrestricted: false,
    // As the gate decides: a read (the service's own pattern), or a change a held role allows; anything else is an admin's.
    may: (method) => {
      if (!index) return false;
      if (index.reads.test(method)) return true;
      return rowOf(method)?.roles.some((r) => held.includes(r)) ?? false;
    },
    caption: (method) => {
      const roles = rowOf(method)?.roles ?? [];
      return roles.length ? `Needs the ${joinRoles(roles, 'or')} staff role. A MotionSafe admin can give it to you.` : ADMIN_CAPTION;
    },
  };
}
